import { useBlockProps, BlockControls, RichText } from "@wordpress/block-editor";
import { ToolbarGroup, Button as WPButton } from "@wordpress/components";
import { TextControl } from "@wordpress/components";
import { t } from "drupal";
import NoImageCardSidePanel from "./sidePanel";
import { handleUrlChange2, sanitizeInput, sanitizeKeepHTML, isValidDomain } from '../utilities/helpers';
import { useState, useEffect, useCallback } from "@wordpress/element";


function Edit({ attributes, setAttributes }) {

  const {
    title = "",
    content = "",
    link = "",
    linkText = "",
    backgroundColorClass = "",
    ariaLabel = '',
    titleError = "",
    ctaUrlError = false,
    ctaError = "",
  } = attributes;


  // console.log("No-image card edit: ", { title, content, link, linkText, backgroundColorClass });

  // Update functions

  const onChangeBackgroundColorClass = (className) =>
    setAttributes({ backgroundColorClass: className });

  const ALLOWED_BLOCKS = ['core/image'];
  const TEMPLATE = [
    ['core/image', {}]
  ];

  // Strip out HTML and script for title
  const onChangeTitle = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ title: cleanedText });
  };

  // Strip out HTML and script for CTA
  const onChangeLinkText = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ linkText: cleanedText });
  };

  // Strip out HTML and script for CTA
  const onChangeLink = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ link: cleanedText });
  };

  // Strip out script, keep HTML for content
  const onChangeContent = (value) => {
    const cleanedText = sanitizeKeepHTML(value);
    setAttributes({ content: cleanedText });
  };

  // For side panel input: strip out both <script> and all HTML to avoid crashes
  const onChangeContentSidePanel = (value) => {
    const cleanedText = sanitizeInput(value);
    setAttributes({ content: cleanedText });
  };

  /**
   * Dynamically checks for requried fields, update attributes with error flags that validation.js watches, and auto disable
   * save button when missing title linktext, linkURL, or iamge
   **/

  useEffect(() => {

    const trimmedLink = link?.trim();
    const trimmedLinkText = linkText?.trim();

    const titleError = !title?.trim();

    // Only set ctaUrlError if user has typed a link
    const ctaUrlError = trimmedLink ? !isValidDomain(trimmedLink) : false;

    // Only set ctaError if user has typed a link but no CTA text
    const ctaError = trimmedLink && !trimmedLinkText;

    setAttributes({
      titleError,
      ctaUrlError,
      ctaError,
    });
  }, [title, link, linkText]);


  /**
   * If the user presses Enter or Tab, update the URL
   **/

  const handleKeyDown = useCallback((event) => {
    if ((event.key === 'Enter' || event.key === 'Tab') && link) {
      //handleUrlChange2 is from /deohs_gutenberg_blocks/blocks/utilities/helpers.js
      const updatedUrl = handleUrlChange2(link);
      if (updatedUrl !== link) { // Prevent unnecessary updates
        setAttributes({ link: updatedUrl });
      }
    }
  }, [link, setAttributes]);

  return (
    <div {...useBlockProps()}>
      <BlockControls>
        <ToolbarGroup>
          <WPButton label={t("No-image card Settings")} icon="heading" disabled />
        </ToolbarGroup>
      </BlockControls>

      {(titleError) && (
        <div className="editor-field-error-message">
          {titleError ? t("Card title is required") : ""}
        </div>
      )}

      {/* Display error message if URL is invalid */}
      {ctaUrlError && (
        <div className="editor-field-error-message">
          {t("If you provide a URL, please provide a valid absoloute or relative URL.")}
        </div>
      )}

      {/* Display error message if CTA is empty if URL is provided */}
      {ctaError && (
        <div className="editor-field-error-message">
          {t("CTA text is required when URL is provided")}
        </div>
      )}


      <div className="col">

        <div className={`card horizontal_img h-100 ${backgroundColorClass}`}>
          <div className="card-img-top">
          </div>

          <div class="card-body">

            {/* title is required  */}
            <TextControl
              className={`h3 card-title h4 ${titleError ? "editor-field-error is-required" : ""}`}
              value={title}
              onChange={(value) => {
                const trimmedValue = value?.trim() ?? "";
                onChangeTitle(value, "title");
              }}
              placeholder={t("Card title")}
            />
            <RichText
              tagName="p"
              className="card-text"
              value={content}
              onChange={onChangeContent}
              placeholder={t("Card content")}
            />
            <TextControl
              value={link}
              onChange={onChangeLink}
              className={ctaUrlError ? "editor-field-error" : ""}
              onBlur={() => {
                if (link?.trim()) {
                  const updatedUrl = handleUrlChange2(link.trim());
                  if (updatedUrl !== link.trim()) {
                    setAttributes({ link: updatedUrl });
                  }
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("/about or example.com")}
              aria-invalid={ctaUrlError}
            />

            <TextControl
              className={ctaError ? "editor-field-error btn btn-link" : "btn btn-link"}
              value={linkText}
              onChange={onChangeLinkText}
              placeholder={t("Your call to action")}
            />
          </div>
        </div>
      </div>

      <NoImageCardSidePanel
        title={title}
        content={content}
        link={link}
        linkText={linkText}
        onChangeTitle={onChangeTitle}
        // handle content sanization between side panel and editor differently
        onChangeContent={onChangeContentSidePanel}
        onChangeLink={onChangeLink}
        onChangeLinkText={onChangeLinkText}
        onChangeBackgroundColorClass={onChangeBackgroundColorClass}
        ariaLabel={ariaLabel}
        setAttributes={setAttributes}

      />
    </div>
  );
}

export default Edit;
